import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { Button, InlineError, SectionCard, Skeleton } from '@/components/ui'
import { TwitchConnectorPanel } from '@/components/twitch/TwitchConnectorPanel'
import { TwitchFeed } from '@/components/twitch/TwitchFeed'
import { TwitchMessageActions } from '@/components/twitch/TwitchMessageActions'
import { TwitchMessageComposer } from '@/components/twitch/TwitchMessageComposer'
import { connectionStateLabel, startTwitchAuthorization, TIMEOUT_OPTIONS, useTwitchConnection } from '@/components/twitch/use-twitch-connection'
import { DancefloorControlPanel } from '@/components/dancefloor/DancefloorControlPanel'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { apiClient, scopedApiPath } from '@/lib/api-client'
import { useViewAsStore } from '@/stores/view-as-store'
import type { ChannelsResponse } from '@/lib/types'

export const Route = createFileRoute('/admin/chat')({
  validateSearch: (search: Record<string, unknown>): { channel?: number; twitch?: 'connected' | 'error' } => {
    const channel = Number(search.channel)
    return {
      ...(Number.isSafeInteger(channel) && channel > 0 ? { channel } : {}),
      ...(search.twitch === 'connected' || search.twitch === 'error' ? { twitch: search.twitch } : {}),
    }
  },
  component: AdminChatPage,
})

function AdminChatPage() {
  useDocumentTitle('Chat')
  const viewAsId = useViewAsStore(s => s.viewAsId)
  const connection = useTwitchConnection(viewAsId)
  const { channel: selectedId } = Route.useSearch()
  const [moderationBusy, setModerationBusy] = useState(false)
  const [moderationError, setModerationError] = useState<string | null>(null)
  const [moderationNotice, setModerationNotice] = useState<string | null>(null)
  const [reconnecting, setReconnecting] = useState(false)
  const moderationPending = useRef(false)
  const channels = useQuery({
    queryKey: ['admin', 'channels', viewAsId == null ? '' : String(viewAsId)],
    queryFn: () => apiClient.get<ChannelsResponse>(scopedApiPath('/system/channels/', viewAsId)),
  })
  // Destination metadata is only used to link the OAuth account to an output.
  // The connected broadcaster is the sole source of the monitored chat identity.
  const destinations = (channels.data?.channels ?? []).filter(c => c.platform?.toLowerCase() === 'twitch')
  const destination = destinations.find(c => c.id === selectedId)
    ?? destinations.find(c => c.enabled) ?? destinations[0]
  const result = connection.data
  const account = result?.connection
  const accountLogin = account?.login
  const login = typeof accountLogin === 'string' && /^[a-z0-9_]{4,25}$/.test(accountLogin) ? accountLogin : null
  const authorized = account?.authorization === 'authorized' || (!!account && !account.authorization && !!login &&
    ['connected', 'connecting', 'paused', 'needs_chat_permission', 'waiting_for_bot'].includes(account.state))

  const reconnect = async () => {
    setReconnecting(true)
    try { await startTwitchAuthorization(viewAsId, destination?.id) }
    catch (err) { toast.error(err instanceof Error ? err.message : 'Twitch request failed.') }
    finally { setReconnecting(false) }
  }

  /** Runs one moderation call; throws so a confirm dialog can stay open with the error. */
  async function moderate(action: string, body: Record<string, unknown>, success: string | null) {
    if (moderationPending.current) return
    moderationPending.current = true
    setModerationBusy(true)
    setModerationError(null)
    setModerationNotice(null)
    try {
      await apiClient.post('/twitch/', { action, ...body })
      await connection.refetch()
      if (success) setModerationNotice(success)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Twitch moderation failed. Try again.'
      setModerationError(message)
      throw err
    } finally {
      moderationPending.current = false
      setModerationBusy(false)
    }
  }
  const quietly = (promise: Promise<void>) => promise.catch(() => { /* surfaced through moderationError */ })

  const connectorStrip = account && !connection.isLoading && !connection.isError ? (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-card px-3 py-2 text-[13px] xl:hidden">
      <p role="status" className="min-w-0 truncate">
        <span className="text-muted-foreground">Connector · </span>
        {login ? `@${login} · ` : ''}{connectionStateLabel(account.state)}
      </p>
      <div className="flex items-center gap-2">
        {result?.can_manage && account.configured && <Button type="button" size="sm" variant={authorized ? 'outline' : 'default'} disabled={reconnecting} onClick={() => void reconnect()}>
          {login ? 'Reconnect' : 'Connect Twitch'}
        </Button>}
        <a href="#twitch-connector" className="inline-flex min-h-9 items-center px-1 text-muted-foreground underline underline-offset-4 hover:text-foreground">Details</a>
      </div>
    </div>
  ) : null

  return (
    <div data-testid="admin-chat" className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
      <div className="space-y-6">
        {connectorStrip}
        <SectionCard header={
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-foreground">{login ? `@${login} · Twitch chat` : 'Your Twitch channel'}</h2>
              <p className="mt-0.5 text-[13px] text-muted-foreground">Monitor and send chat for your connected Twitch account.</p>
            </div>
            <Button asChild variant="outline" size="sm"><Link to="/admin/dancefloor" data-testid="admin-chat-open-dancefloor">Open dancefloor</Link></Button>
          </div>
        }>
          {connection.isLoading ? <Skeleton className="h-[460px] w-full" />
            : connection.isError ? <InlineError message="Could not load your Twitch account. Retrying…" />
            : !login || !result || !account || !authorized ? <div data-testid="admin-chat-disconnected" className="rounded-md border border-dashed border-border px-4 py-8 text-center">
              <p className="text-sm text-muted-foreground">{viewAsId == null
                ? 'Connect the Twitch account you stream from using the connector. Your channel’s chat will appear here automatically.'
                : 'This account has not connected its Twitch channel yet.'}</p>
            </div> : <div key={`${viewAsId ?? 'self'}-${login}`} className="space-y-4">
              {result.feed.available ? <>
                <TwitchFeed login={login} messages={result.feed.messages} actions={message => result.can_manage ? <TwitchMessageActions message={message} login={login}
                  canDelete={account.permissions.delete} canModerate={account.permissions.moderate} busy={moderationBusy}
                  onDelete={item => quietly(moderate('delete_message', { message_id: item.id }, 'Message deleted.'))}
                  onTimeout={(item, seconds) => quietly(moderate('timeout_user', { message_id: item.id, duration: seconds }, null)
                    .then(() => { toast.success(`${item.user_name} timed out for ${TIMEOUT_OPTIONS.find(option => option.seconds === seconds)?.spoken ?? `${seconds} seconds`}.`) }))}
                  onBan={item => moderate('ban_user', { message_id: item.id }, `${item.user_name} banned from @${login}.`)} /> : null} />
                <TwitchMessageComposer key={`${viewAsId ?? 'self'}-${login}`} login={login} botLogin={account.bot.login}
                  permissions={account.permissions} enabled={authorized && result.can_manage} onSent={() => connection.refetch()}
                  onReconnect={() => startTwitchAuthorization(viewAsId, destination?.id)} location="main" />
                {moderationError && <InlineError message={moderationError} />}
                {moderationNotice && <p role="status" className="text-[13px] text-cyber-green">{moderationNotice}</p>}
              </> : <div data-testid="admin-chat-unavailable" className="rounded-md border border-dashed border-border px-4 py-8 text-center">
                <p className="text-sm text-muted-foreground">Chat feed is unavailable. Check the connector status and reconnect Twitch if access is needed.</p>
              </div>}
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" asChild><a href={`https://www.twitch.tv/popout/${login}/chat?popout=`}
                  target="_blank" rel="noopener noreferrer" data-testid="admin-chat-popout">Open my chat in a new window</a></Button>
                <Button variant="outline" size="sm" asChild><a href={`https://www.twitch.tv/${login}`}
                  target="_blank" rel="noopener noreferrer" data-testid="admin-chat-open">Open channel on Twitch</a></Button>
              </div>
            </div>}
        </SectionCard>
        {login && result && <SectionCard title="Dancefloor controls"
          description="Steer the Digital Dancefloor from here: broadcast settings, crowd cues sent as you, and the commands your viewers can use.">
          <DancefloorControlPanel key={`${viewAsId ?? 'self'}-${login}`} login={login} feed={result.feed}
            permissions={result.connection.permissions} botLogin={result.connection.bot.login} canManage={result.can_manage}
            onPreferencesChange={async preferences => {
              await apiClient.post('/twitch/', { action: 'dancefloor_settings', set: preferences.set, avatar: preferences.avatar ?? null,
                demo_crowd: preferences.demo_crowd ?? false, hide_commands: preferences.hide_commands ?? false, ...(preferences.camera ? { camera: preferences.camera } : {}) })
              await connection.refetch()
            }}
            onReconnect={() => startTwitchAuthorization(viewAsId, destination?.id)} />
        </SectionCard>}
      </div>
      <div id="twitch-connector" className="scroll-mt-20">
        <TwitchConnectorPanel key={viewAsId ?? 'self'} viewAsId={viewAsId} channelId={destination?.id} showChatFeed={false} />
      </div>
    </div>
  )
}
